import React, { useState, useEffect } from 'react';
import agentApiService from '../../services/agentApiService';
import './TokenHistory.css';

const TokenHistory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10,
    hasNext: false,
    hasPrevious: false
  });
  const [sortBy, setSortBy] = useState('rechargedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredHistory, setFilteredHistory] = useState([]);

  useEffect(() => {
    fetchTokenHistory();
    fetchTokenHistoryStats();
  }, [pagination.currentPage, pagination.size, sortBy, sortDir]);

  useEffect(() => {
    // Filter history based on search term
    if (searchTerm.trim() === '') {
      setFilteredHistory(history);
    } else {
      const filtered = history.filter(item =>
        (item.tokenId && item.tokenId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.rechargeAmount && item.rechargeAmount.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.noOfTokensRecharge && item.noOfTokensRecharge.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.rechargedDate && item.rechargedDate.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.rechargeOperatorName && item.rechargeOperatorName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.rechargeOperatorId && item.rechargeOperatorId.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredHistory(filtered);
    }
  }, [history, searchTerm]);

  const fetchTokenHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📜 [TOKEN-HISTORY] Fetching token recharge history...', {
        page: pagination.currentPage,
        size: pagination.size,
        sortBy,
        sortDir
      });
      
      const response = await agentApiService.getTokenRechargeHistory(
        pagination.currentPage,
        pagination.size,
        sortBy,
        sortDir
      );
      
      if (response.success) {
        setHistory(response.history || []);
        setPagination(response.pagination || pagination);
        console.log('✅ [TOKEN-HISTORY] Token history loaded successfully:', response.history?.length, 'records');
      } else {
        throw new Error(response.message || 'Failed to fetch token history');
      }
    } catch (err) {
      console.error('❌ [TOKEN-HISTORY] Error fetching token history:', err);
      setError(err.message || 'Failed to load token history');
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenHistoryStats = async () => {
    try {
      console.log('📊 [TOKEN-HISTORY] Fetching token history statistics...');
      const response = await agentApiService.getTokenRechargeHistoryStats();
      
      if (response.success) {
        setStats(response.stats);
        console.log('✅ [TOKEN-HISTORY] Token history statistics loaded successfully');
      } else {
        console.warn('⚠️ [TOKEN-HISTORY] Failed to fetch statistics:', response.message);
      }
    } catch (err) {
      console.error('❌ [TOKEN-HISTORY] Error fetching token history statistics:', err);
      // Don't set error for stats failure, just log it
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDir('desc');
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setPagination(prev => ({ ...prev, size: newSize, currentPage: 0 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortDir === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="token-history-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading token recharge history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-history-container">
        <div className="error-message">
          <h3>Error Loading Token History</h3>
          <p>{error}</p>
          <button onClick={fetchTokenHistory} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="token-history-container">
      {/* Header */}
      <div className="page-header">
        <h1>Token Recharge History</h1>
        <p>View your complete token recharge transaction history</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>Total Recharges</h3>
              <p className="stat-value">{stats.totalRecharges}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>Total Amount Spent</h3>
              <p className="stat-value">${stats.totalAmountSpent?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🪙</div>
            <div className="stat-content">
              <h3>Total Tokens Purchased</h3>
              <p className="stat-value">{stats.totalTokensPurchased}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-content">
              <h3>Average Recharge</h3>
              <p className="stat-value">${stats.averageRechargeAmount?.toFixed(2) || '0.00'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="controls-section">
        <div className="search-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by Token ID, Amount, Date, or Operator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
        
        <div className="pagination-controls">
          <label>
            Show:
            <select value={pagination.size} onChange={handlePageSizeChange} className="page-size-select">
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            entries
          </label>
        </div>
      </div>

      {/* History Table */}
      <div className="history-table-container">
        <div className="table-card">
          <div className="table-header">
            <h3>Token Recharge History</h3>
            <div className="table-info">
              Showing {filteredHistory.length} of {pagination.totalElements} entries
            </div>
          </div>
          
          <div className="table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th onClick={() => handleSortChange('tokenId')} className="sortable">
                    Token ID {getSortIcon('tokenId')}
                  </th>
                  <th onClick={() => handleSortChange('rechargeAmount')} className="sortable">
                    Token Amount {getSortIcon('rechargeAmount')}
                  </th>
                  <th onClick={() => handleSortChange('noOfTokensRecharge')} className="sortable">
                    No of Tokens {getSortIcon('noOfTokensRecharge')}
                  </th>
                  <th onClick={() => handleSortChange('rechargedDate')} className="sortable">
                    Recharge Date {getSortIcon('rechargedDate')}
                  </th>
                  <th>Recharge Operator Name</th>
                  <th>Recharge Operator ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((item, index) => (
                    <tr key={item.tokenId || index} className="table-row">
                      <td className="token-id">{item.tokenId || 'N/A'}</td>
                      <td className="amount">${item.rechargeAmount || '0.00'}</td>
                      <td className="tokens">{item.noOfTokensRecharge || '0'}</td>
                      <td className="date">{formatDate(item.rechargedDate)}</td>
                      <td className="operator-name">{item.rechargeOperatorName || 'N/A'}</td>
                      <td className="operator-id">{item.rechargeOperatorId || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-data">
                      {searchTerm ? 'No matching records found' : 'No token recharge history available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {pagination.currentPage + 1} of {pagination.totalPages}
            ({pagination.totalElements} total entries)
          </div>
          
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(0)}
              disabled={!pagination.hasPrevious}
              className="pagination-button"
              title="First Page"
            >
              ⏮️
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevious}
              className="pagination-button"
              title="Previous Page"
            >
              ⬅️
            </button>
            
            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const startPage = Math.max(0, pagination.currentPage - 2);
              const pageNum = startPage + i;
              if (pageNum >= pagination.totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-button ${pageNum === pagination.currentPage ? 'active' : ''}`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="pagination-button"
              title="Next Page"
            >
              ➡️
            </button>
            <button
              onClick={() => handlePageChange(pagination.totalPages - 1)}
              disabled={!pagination.hasNext}
              className="pagination-button"
              title="Last Page"
            >
              ⏭️
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenHistory;